import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, Plus, Copy, Trash2, Loader2, Star, Wand2, Layers,
} from "lucide-react";

const MODE_TONE = {
  COMMERCIAL_QUOTE: "bg-indigo-50 text-indigo-700 border-indigo-200",
  SOLUTION_PROPOSAL: "bg-violet-50 text-violet-700 border-violet-200",
  PROFORMA: "bg-amber-50 text-amber-700 border-amber-200",
  CATALOGUE: "bg-teal-50 text-teal-700 border-teal-200",
};

export default function DocumentTemplates() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);      // template being edited/created
  const [resolveOpen, setResolveOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        api.get("/document-templates/meta"),
        api.get("/document-templates"),
      ]);
      setMeta(m.data); setItems(l.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const newTemplate = () => {
    const columns = Object.fromEntries((meta?.columns || []).map((c) => [c, true]));
    const stdSecs = ["intro", "product_content", "commercial_terms", "bank_details", "closing", "signature"];
    const sections = Object.fromEntries((meta?.sections || []).map((s) => [s, stdSecs.includes(s)]));
    const rules = Object.fromEntries((meta?.rule_keys || []).map((r) => [r, true]));
    setEdit({ __new: true, name: "", mode: "COMMERCIAL_QUOTE", currency: "ANY",
      presentation: "COMPACT_SPEC", columns, sections, rules, is_default: false, status: "ACTIVE" });
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template ${t.name}?`)) return;
    try { await api.delete(`/document-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const duplicate = async (t) => {
    try { await api.post(`/document-templates/${t.id}/duplicate`); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="document-templates-page">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-6 w-6 text-indigo-700" /> Document Templates
          </h1>
          <p className="text-sm text-slate-500">Configuration Studio · Commercial. Templates drive document modes, visible columns, product presentation, and which sections appear — one template, many documents.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setResolveOpen(true)} data-testid="doc-tpl-resolve-btn"><Wand2 className="h-4 w-4 mr-1" /> Test auto-select</Button>
          <Button variant="create" className="" onClick={newTemplate} data-testid="doc-tpl-add-btn"><Plus className="h-4 w-4 mr-1" /> New template</Button>
        </div>
      </div>

      {loading ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="doc-tpl-grid">
          {items.map((t) => (
            <div key={t.id} className="border border-slate-200 rounded-xl p-4 bg-white hover:border-slate-300 transition-colors" data-testid={`doc-tpl-card-${t.code}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold text-slate-900 flex items-center gap-1.5">
                    {t.name}
                    {t.is_default && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400" />}
                  </div>
                  <div className="text-[10px] font-mono text-slate-400">{t.code}</div>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border ${MODE_TONE[t.mode] || "bg-slate-100"}`}>{t.mode.replace("_", " ")}</span>
              </div>
              <div className="flex gap-1.5 mt-2 flex-wrap text-[10px]">
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.currency}</span>
                <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{t.presentation?.replace("_", " ")}</span>
                <span className={`px-1.5 py-0.5 rounded ${t.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{t.status}</span>
              </div>
              <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1"><Layers className="h-3 w-3" />{Object.values(t.sections || {}).filter(Boolean).length} sections · {Object.values(t.columns || {}).filter(Boolean).length} columns</div>
              <div className="flex gap-1 mt-3">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setEdit(t)} data-testid={`doc-tpl-edit-${t.code}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => duplicate(t)} data-testid={`doc-tpl-dup-${t.code}`}><Copy className="h-3.5 w-3.5" /></Button>
                {!t.seeded && <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => remove(t)} data-testid={`doc-tpl-del-${t.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && meta && <TemplateEditor meta={meta} initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {resolveOpen && <ResolveTester onClose={() => setResolveOpen(false)} />}
    </div>
  );
}

function TemplateEditor({ meta, initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [f, setF] = useState({
    name: initial.name || "", mode: initial.mode, currency: initial.currency,
    presentation: initial.presentation, description: initial.description || "",
    is_default: !!initial.is_default, status: initial.status || "ACTIVE",
    columns: { ...(initial.columns || {}) }, sections: { ...(initial.sections || {}) },
    rules: { ...(initial.rules || {}) },
  });
  const [busy, setBusy] = useState(false);
  const u = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const toggleMap = (map, key) => setF((s) => ({ ...s, [map]: { ...s[map], [key]: !s[map][key] } }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const body = { ...f };
      if (isNew) await api.post("/document-templates", body);
      else await api.put(`/document-templates/${initial.id}`, body);
      toast.success(isNew ? "Template created" : "Template saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const Sel = ({ k, opts }) => (
    <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f[k]} onChange={(e) => u(k, e.target.value)} data-testid={`doc-tpl-${k}`}>
      {opts.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="doc-tpl-editor">
        <DialogHeader><DialogTitle>{isNew ? "New template" : `Edit ${initial.name}`}{initial.seeded && <span className="text-[10px] ml-2 text-slate-400">(built-in)</span>}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-[12px]">Name</Label><Input value={f.name} onChange={(e) => u("name", e.target.value)} data-testid="doc-tpl-name" /></div>
            <div><Label className="text-[12px]">Document mode</Label><Sel k="mode" opts={meta.modes} /></div>
            <div><Label className="text-[12px]">Currency</Label><Sel k="currency" opts={meta.currencies} /></div>
            <div><Label className="text-[12px]">Product presentation</Label><Sel k="presentation" opts={meta.presentations} /></div>
            <div><Label className="text-[12px]">Status</Label><Sel k="status" opts={["ACTIVE", "INACTIVE"]} /></div>
            <div className="col-span-2"><Label className="text-[12px]">Description</Label><Input value={f.description} onChange={(e) => u("description", e.target.value)} /></div>
            <label className="col-span-2 flex items-center gap-2 text-[13px]">
              <Switch checked={f.is_default} onCheckedChange={(v) => u("is_default", v)} data-testid="doc-tpl-default" />
              Default template for this mode (auto-selected)
            </label>
          </div>

          <div>
            <Label className="text-[12px] font-semibold">Visible columns</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {meta.columns.map((col) => (
                <label key={col} className="flex items-center gap-2 text-[12px] border border-slate-200 rounded px-2 py-1.5 cursor-pointer" data-testid={`doc-tpl-col-${col}`}>
                  <input type="checkbox" checked={f.columns[col] ?? false} onChange={() => toggleMap("columns", col)} />
                  {meta.column_labels[col] || col}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[12px] font-semibold">Sections</Label>
            <div className="grid grid-cols-3 gap-1.5 mt-1">
              {meta.sections.map((sec) => (
                <label key={sec} className="flex items-center gap-2 text-[12px] border border-slate-200 rounded px-2 py-1.5 cursor-pointer" data-testid={`doc-tpl-sec-${sec}`}>
                  <input type="checkbox" checked={f.sections[sec] ?? false} onChange={() => toggleMap("sections", sec)} />
                  {meta.section_labels[sec] || sec}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[12px] font-semibold">Conditional rules</Label>
            <div className="grid grid-cols-1 gap-1.5 mt-1">
              {meta.rule_keys.map((rk) => (
                <label key={rk} className="flex items-center justify-between text-[12px] border border-slate-200 rounded px-2 py-1.5">
                  <span>{meta.rule_labels[rk] || rk}</span>
                  <Switch checked={f.rules[rk] ?? true} onCheckedChange={() => toggleMap("rules", rk)} data-testid={`doc-tpl-rule-${rk}`} />
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={save} disabled={busy} data-testid="doc-tpl-save">{busy ? "Saving…" : "Save template"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResolveTester({ onClose }) {
  const [f, setF] = useState({ mode: "COMMERCIAL_QUOTE", currency: "INR", has_services: false });
  const [result, setResult] = useState(null);
  const run = async () => {
    try { const { data } = await api.post("/document-templates/resolve", f); setResult(data.template); }
    catch (e) { setResult(null); toast.error(e.response?.data?.detail || "No match"); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="doc-tpl-resolve-dialog">
        <DialogHeader><DialogTitle>Test template auto-selection</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[12px]">Mode</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} data-testid="doc-tpl-resolve-mode">
                {["COMMERCIAL_QUOTE", "SOLUTION_PROPOSAL", "PROFORMA", "CATALOGUE"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select></div>
            <div><Label className="text-[12px]">Currency</Label>
              <select className="w-full h-9 border border-slate-200 rounded-md px-2 text-[13px] bg-white" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} data-testid="doc-tpl-resolve-currency">
                {["INR", "USD"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select></div>
          </div>
          <label className="flex items-center gap-2 text-[13px]">
            <Switch checked={f.has_services} onCheckedChange={(v) => setF({ ...f, has_services: v })} data-testid="doc-tpl-resolve-services" />
            Quote includes services
          </label>
          {result && (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-3" data-testid="doc-tpl-resolve-result">
              <div className="text-[11px] text-emerald-700 uppercase tracking-wider">Selected template</div>
              <div className="font-semibold">{result.name} <span className="font-mono text-[11px] text-slate-500">{result.code}</span></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={run} data-testid="doc-tpl-resolve-run">Resolve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
